import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import LoginForm from './components/LoginForm';
import SecurityBadges from './components/SecurityBadges';
import TestCredentials from './components/TestCredentials';
import RegistrationPrompt from './components/RegistrationPrompt';
import { Link } from 'react-router-dom';

const Login = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Login - TrainaryGym</title>
        <meta name="description" content="Sign in to your TrainaryGym account to access your fitness dashboard, book classes, and track your progress." />
      </Helmet>

      <div className="min-h-screen relative flex items-center justify-center p-4 md:p-6 lg:p-8">
        {/* Content */}
        <div className="relative z-10 w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left Section - Branding & Info */}
          <div className="flex flex-col justify-center space-y-6 md:space-y-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon name="Dumbbell" size={28} color="var(--color-primary)" />
                </div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground">
                  TrainaryGym
                </h1>
              </div>
              <p className="text-base md:text-lg text-muted-foreground max-w-md">
                Your complete gym management solution. Access your dashboard, book classes, track workouts, and achieve your fitness goals.
              </p>
            </div>

            <div className="hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-2xl">
                <Image
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1b9cf2b3f-1766499443104.png"
                  alt="Modern gym interior with state-of-the-art equipment, bright lighting, and professional training area showcasing fitness facility"
                  className="w-full h-full object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center gap-3 text-foreground">
                    <Icon name="TrendingUp" size={24} color="var(--color-success)" />
                    <div>
                      <div className="text-sm font-medium">Join 10,000+ Members</div>
                      <div className="text-xs text-muted-foreground">Achieving their fitness goals</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <SecurityBadges />
          </div>

          {/* Right Section - Login Form */}
          <div className="flex flex-col justify-center">
            <div className="w-full max-w-md mx-auto space-y-6 md:space-y-8">
              <div className="card">
                <div className="space-y-2 mb-6 md:mb-8">
                  <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground">
                    Welcome Back
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Sign in to access your account
                  </p>
                </div>

                <LoginForm />

                <div className="mt-6 md:mt-8 pt-6 border-t border-border">
                  <TestCredentials />
                </div>
              </div>

              <RegistrationPrompt />

              {/* System Administrator Login Link */}
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900/5 dark:bg-gray-100/5 border border-gray-300 dark:border-gray-700">
                  <Icon name="Shield" size={16} color="var(--color-muted-foreground)" />
                  <span className="text-sm text-muted-foreground">System Administrator?</span>
                  <button
                    onClick={() => navigate('/system-administrator-login')}
                    className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors duration-250 focus-ring rounded px-2 py-1">

                    Click here to login
                  </button>
                </div>
              </div>

              {/* Gym Owner Signup Link */}
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/5 border border-primary/20">
                  <Icon name="Building2" size={16} color="var(--color-primary)" />
                  <span className="text-sm text-muted-foreground">Want to manage your gym?</span>
                  <button
                    onClick={() => navigate('/gym-owner-signup')}
                    className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors duration-250 focus-ring rounded px-2 py-1">

                    Sign up as Gym Owner
                  </button>
                </div>
              </div>

              <div className="text-center">
                <p className="text-xs text-muted-foreground">
                  By signing in, you agree to our{' '}
                  <Link to="/terms-and-conditions" className="text-primary hover:text-primary/80 transition-colors duration-250 focus-ring rounded px-1" target="_blank">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy-policy" className="text-primary hover:text-primary/80 transition-colors duration-250 focus-ring rounded px-1" target="_blank">
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>);

};

export default Login;